<template>
  <app-layout title="Riwayat Evaluasi">
    <template #header>
      <div class="flex items-center justify-between">
        <h2 class="font-semibold text-xl text-gray-800 leading-tight">
          Riwayat Evaluasi - {{ dokumenPengadaan?.isi_doc || 'Dokumen Pengadaan' }}
        </h2>
        <Link :href="route('dokumen-pengadaan.pengajuan', dokumenPengadaan.id)">
          <Button label="Kembali" icon="pi pi-arrow-left" severity="secondary" size="small" />
        </Link>
      </div>
    </template>

    <div class="py-12">
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
        <!-- Info Banner -->
        <div class="mb-6 bg-amber-50 border-l-4 border-amber-400 p-4 rounded-lg shadow-sm">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="pi pi-info-circle text-amber-400 text-xl"></i>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-amber-800">
                Data Riwayat Evaluasi
              </h3>
              <div class="mt-2 text-sm text-amber-700">
                <p>
                  Data ini adalah arsip dari evaluasi sebelumnya yang di-reset karena sanggah diterima 
                  dengan opsi "Evaluasi Ulang". Data ini bersifat read-only untuk keperluan historis.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Sanggah Info -->
        <div v-if="riwayat?.sanggah" class="mb-6 bg-white rounded-lg shadow p-6">
          <h3 class="font-semibold text-gray-700 mb-4 flex items-center">
            <i class="pi pi-exclamation-triangle text-amber-500 mr-2"></i>
            Informasi Sanggah
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div class="bg-gray-50 p-3 rounded">
              <span class="text-gray-500 block mb-1">Penyanggah:</span>
              <span class="font-medium text-gray-900">
                {{ penyanggahInfo?.nama_perusahaan || riwayat.sanggah.dokumen_pengadaan_actor?.user?.fullname || '-' }}
              </span>
            </div>
            <div class="bg-gray-50 p-3 rounded">
              <span class="text-gray-500 block mb-1">Tanggal Sanggah:</span>
              <span class="font-medium text-gray-900">{{ formatDate(riwayat.sanggah.created_at || riwayat.sanggah.updated_at) }}</span>
            </div>
            <div class="bg-gray-50 p-3 rounded">
              <span class="text-gray-500 block mb-1">Tipe Penerimaan:</span>
              <span class="px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                {{ formatTipeDiterima(riwayat.sanggah.tipe_diterima) }}
              </span>
            </div>
            <div class="bg-gray-50 p-3 rounded">
              <span class="text-gray-500 block mb-1">Diterima Oleh:</span>
              <span class="font-medium text-gray-900">
                {{ riwayat.created_by?.fullname || riwayat.summary_snapshot?.dibuat_oleh || 'System' }}
              </span>
            </div>
          </div>
          <div class="mt-4">
            <span class="text-gray-500 text-sm block mb-1">Alasan Sanggah:</span>
            <p class="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-200">
              {{ riwayat.sanggah.alasan || '-' }}
            </p>
          </div>
          <div v-if="riwayat.sanggah.jawaban" class="mt-3">
            <span class="text-gray-500 text-sm block mb-1">Jawaban Sanggah:</span>
            <p class="text-sm text-gray-700 bg-green-50 p-3 rounded border border-green-200">
              {{ riwayat.sanggah.jawaban }}
            </p>
          </div>
        </div>

        <!-- BAEP & BAHP Info -->
        <div class="mb-6 bg-white rounded-lg shadow p-6">
          <h3 class="font-semibold text-gray-700 mb-4 flex items-center">
            <i class="pi pi-file-pdf text-red-500 mr-2"></i>
            Berita Acara (Arsip)
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- BAEP -->
            <div class="border border-gray-200 rounded-lg p-4">
              <h4 class="font-medium text-gray-800 mb-3">Berita Acara Evaluasi Penawaran (BAEP)</h4>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-500">Nomor BAEP:</span>
                  <span class="font-medium text-gray-900">{{ riwayat.summary_snapshot?.nomor_baep || '-' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Tanggal BAEP:</span>
                  <span class="font-medium text-gray-900">{{ formatDateOnly(riwayat.summary_snapshot?.tanggal_baep) }}</span>
                </div>
              </div>
            </div>
            <!-- BAHP -->
            <div class="border border-gray-200 rounded-lg p-4">
              <h4 class="font-medium text-gray-800 mb-3">Berita Acara Hasil Pemilihan (BAHP)</h4>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-500">Nomor BAHP:</span>
                  <span class="font-medium text-gray-900">{{ riwayat.summary_snapshot?.nomor_bahp || '-' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Tanggal BAHP:</span>
                  <span class="font-medium text-gray-900">{{ formatDateOnly(riwayat.summary_snapshot?.tanggal_bahp) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Historic Evaluation Summary Component -->
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
          <HistoricEvaluationSummary :riwayat="riwayat" :dokumenPengadaan="dokumenPengadaan" />
        </div>

        <!-- Metadata -->
        <div class="mt-6 text-sm text-gray-500 text-right">
          <p>Data diarsipkan pada: {{ formatDate(riwayat?.created_at) }}</p>
          <p v-if="riwayat?.created_by">Oleh: {{ riwayat.created_by?.fullname || 'System' }}</p>
        </div>
      </div>
    </div>

    <Toast position="top-right" />
  </app-layout>
</template>

<script>
import { defineComponent } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import { Link } from '@inertiajs/inertia-vue3'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import HistoricEvaluationSummary from '@/Components/DokumenPengadaan/HistoricEvaluationSummary.vue'

export default defineComponent({
  name: 'RiwayatEvaluasiView',

  components: {
    AppLayout,
    Link,
    Button,
    Toast,
    HistoricEvaluationSummary
  },

  props: {
    dokumenPengadaan: {
      type: Object,
      required: true
    },
    riwayat: {
      type: Object,
      required: true
    },
    penyanggahInfo: {
      type: Object,
      default: null
    }
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    formatDateOnly(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    },
    
    formatTipeDiterima(tipe) {
      const labels = {
        'pengadaan_ulang': 'Pengadaan Ulang',
        'evaluasi_ulang': 'Evaluasi Ulang',
        'penawaran_ulang': 'Penawaran Ulang'
      }
      return labels[tipe] || tipe || '-'
    }
  }
})
</script>


